import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  FaDatabase,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSyncAlt,
  FaKey,
  FaCloudUploadAlt,
  FaShieldAlt,
  FaLock,
  FaTimes
} from 'react-icons/fa';
import {
  getStorageStatus,
  provisionDefaultStorage,
  submitBYOStorage,
  validateStorage,
  rotateStorage
} from '../../services/storage.service';
import { showToast } from '../../utils/toast';

const STATUS_BADGES = {
  ready: { label: 'Ready', bg: '#dcfce7', text: '#15803d', border: '#bbf7d0' },
  not_provisioned: { label: 'Not Provisioned', bg: '#f1f5f9', text: '#64748b', border: '#e2e8f0' },
  provisioning: { label: 'Provisioning', bg: '#e0f2fe', text: '#0369a1', border: '#bae6fd' },
  credential_invalid: { label: 'Credential Invalid', bg: '#fee2e2', text: '#b91c1c', border: '#fecaca' },
  migration_pending: { label: 'Migration Pending', bg: '#fef3c7', text: '#b45309', border: '#fde68a' },
  blocked: { label: 'Blocked', bg: '#fee2e2', text: '#b91c1c', border: '#fecaca' },
  hold_blocked: { label: 'Hold Blocked', bg: '#fee2e2', text: '#b91c1c', border: '#fecaca' }
};

export default function OrgStorageManagement({ organizationId, canManage = true }) {
  const [storage, setStorage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(null); // 'provision' | 'validate' | 'byo' | 'rotate'
  const [showByoForm, setShowByoForm] = useState(false);
  const [showRotateForm, setShowRotateForm] = useState(false);
  
  // Write-only input states
  const [byoConnectionString, setByoConnectionString] = useState('');
  const [rotateConnectionString, setRotateConnectionString] = useState('');

  const pollTimerRef = useRef(null);

  const fetchStatus = useCallback(async (silent = false) => {
    if (!organizationId) return;
    if (!silent) setLoading(true);
    try {
      const data = await getStorageStatus(organizationId);
      setStorage(data);
      return data;
    } catch (err) {
      if (!silent) {
        showToast.error(err.message || 'Failed to fetch storage status');
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchStatus();
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [fetchStatus]);

  // Polling for transitional statuses
  useEffect(() => {
    if (storage?.status === 'provisioning' || storage?.status === 'migration_pending') {
      if (!pollTimerRef.current) {
        pollTimerRef.current = setInterval(async () => {
          const updated = await fetchStatus(true);
          if (updated?.status !== 'provisioning' && updated?.status !== 'migration_pending') {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
          }
        }, 3000);
      }
    } else if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, [storage?.status, fetchStatus]);

  const handleProvisionDefault = async () => {
    setActionInProgress('provision');
    try {
      const res = await provisionDefaultStorage(organizationId);
      setStorage(res);
      showToast.success('Default managed storage provisioning requested.');
      fetchStatus(true);
    } catch (err) {
      showToast.error(err.message || 'Failed to provision default storage');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleValidate = async () => {
    setActionInProgress('validate');
    try {
      const res = await validateStorage(organizationId);
      setStorage(res);
      showToast.success('Storage credentials validated successfully.');
    } catch (err) {
      showToast.error(err.message || 'Storage validation failed');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleSubmitByo = async (e) => {
    e.preventDefault();
    if (!byoConnectionString.trim()) {
      showToast.error('Please enter an Azure Blob Storage connection string');
      return;
    }

    const secret = byoConnectionString;
    // CRITICAL: Immediately clear write-only plaintext input from component state
    setByoConnectionString('');
    setShowByoForm(false);
    setActionInProgress('byo');

    try {
      const res = await submitBYOStorage(organizationId, secret);
      setStorage(res);
      showToast.success('Bring-Your-Own storage configured successfully.');
      fetchStatus(true);
    } catch (err) {
      showToast.error(err.message || 'Failed to configure BYO storage');
    } finally {
      setActionInProgress(null);
    }
  };

  const handleRotateByo = async (e) => {
    e.preventDefault();
    if (!rotateConnectionString.trim()) {
      showToast.error('Please enter the replacement Azure Blob Storage connection string');
      return;
    }

    const secret = rotateConnectionString;
    // CRITICAL: Immediately clear write-only plaintext input from component state
    setRotateConnectionString('');
    setShowRotateForm(false);
    setActionInProgress('rotate');

    try {
      const res = await rotateStorage(organizationId, secret);
      setStorage(res);
      showToast.success('Storage credentials rotated successfully.');
      fetchStatus(true);
    } catch (err) {
      showToast.error(err.message || 'Failed to rotate storage credentials');
    } finally {
      setActionInProgress(null);
    }
  };

  const statusKey = storage?.status?.toLowerCase() || 'not_provisioned';
  const badge = STATUS_BADGES[statusKey] || STATUS_BADGES.not_provisioned;

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 8, background: '#f0fdf4', color: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <FaDatabase size={16} />
          </div>
          <div>
            <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Behavioural & Session Storage Profile</h4>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: '#64748b' }}>
              Isolated Azure Blob Storage profile for layout snapshots, session replays, and behavioural telemetry.
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            fontSize: 12,
            fontWeight: 600,
            padding: '4px 10px',
            borderRadius: 6,
            background: badge.bg,
            color: badge.text,
            border: `1px solid ${badge.border}`
          }}>
            {badge.label}
          </span>
          <button
            type="button"
            onClick={() => fetchStatus()}
            disabled={loading || Boolean(actionInProgress)}
            title="Refresh status"
            style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 6, padding: '6px 10px', cursor: 'pointer', color: '#64748b' }}
          >
            <FaSyncAlt className={loading ? 'spin' : ''} size={11} />
          </button>
        </div>
      </div>

      {loading && !storage ? (
        <div style={{ padding: '24px 0', textAlign: 'center', color: '#64748b', fontSize: 13 }}>
          <FaSyncAlt className="spin" size={14} style={{ marginRight: 8 }} /> Loading storage configuration...
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, background: '#f8fafc', padding: 14, borderRadius: 8, border: '1px solid #f1f5f9' }}>
          <div>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Storage Mode</span>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
              {storage?.type === 'byo' ? 'Bring Your Own (BYO Azure)' : 'Cognior Managed (Default)'}
            </div>
          </div>

          <div>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Account Fingerprint</span>
            <div style={{ fontSize: 13, fontFamily: 'monospace', fontWeight: 600, color: '#334155', marginTop: 4 }}>
              {storage?.accountNameFingerprint || '—'}
            </div>
          </div>

          <div>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Health</span>
            <div style={{ fontSize: 13, fontWeight: 600, color: storage?.health === 'healthy' ? '#16a34a' : '#ea580c', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
              {storage?.health === 'healthy' ? <FaCheckCircle size={12} /> : <FaExclamationTriangle size={12} />}
              {storage?.health ? storage.health.toUpperCase() : 'UNKNOWN'}
            </div>
          </div>

          <div>
            <span style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Last Validated</span>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
              {storage?.lastValidatedAtUtc ? new Date(storage.lastValidatedAtUtc).toLocaleString() : 'Never'}
            </div>
          </div>
        </div>
      )}

      {/* Action Controls */}
      {canManage && (
        <div style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {statusKey === 'not_provisioned' && (
            <button
              type="button"
              onClick={handleProvisionDefault}
              disabled={Boolean(actionInProgress)}
              style={{
                background: '#4f46e5',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                padding: '8px 16px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              {actionInProgress === 'provision' ? <FaSyncAlt className="spin" size={12} /> : <FaCloudUploadAlt size={14} />}
              Provision Managed Storage
            </button>
          )}

          {statusKey !== 'not_provisioned' && (
            <button
              type="button"
              onClick={handleValidate}
              disabled={Boolean(actionInProgress)}
              style={{
                background: '#f8fafc',
                color: '#334155',
                border: '1px solid #cbd5e1',
                borderRadius: 6,
                padding: '8px 14px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              {actionInProgress === 'validate' ? <FaSyncAlt className="spin" size={12} /> : <FaCheckCircle size={12} color="#16a34a" />}
              Validate Credentials
            </button>
          )}

          <button
            type="button"
            onClick={() => { setShowByoForm(!showByoForm); setShowRotateForm(false); }}
            disabled={Boolean(actionInProgress)}
            style={{
              background: showByoForm ? '#e2e8f0' : '#f8fafc',
              color: '#334155',
              border: '1px solid #cbd5e1',
              borderRadius: 6,
              padding: '8px 14px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            <FaShieldAlt size={12} color="#6366f1" />
            {storage?.type === 'byo' ? 'Update BYO Storage' : 'Switch to BYO Azure Storage'}
          </button>

          {storage?.type === 'byo' && (
            <button
              type="button"
              onClick={() => { setShowRotateForm(!showRotateForm); setShowByoForm(false); }}
              disabled={Boolean(actionInProgress)}
              style={{
                background: showRotateForm ? '#e2e8f0' : '#f8fafc',
                color: '#334155',
                border: '1px solid #cbd5e1',
                borderRadius: 6,
                padding: '8px 14px',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              <FaKey size={12} color="#f59e0b" />
              Rotate Credentials
            </button>
          )}

          {storage?.type === 'byo' && (
            <button
              type="button"
              onClick={handleProvisionDefault}
              disabled={Boolean(actionInProgress)}
              style={{
                background: '#f8fafc',
                color: '#64748b',
                border: '1px solid #e2e8f0',
                borderRadius: 6,
                padding: '8px 14px',
                fontSize: 12,
                cursor: 'pointer'
              }}
            >
              Revert to Managed Default
            </button>
          )}
        </div>
      )}

      {/* BYO Storage Form */}
      {showByoForm && (
        <form onSubmit={handleSubmitByo} style={{ marginTop: 16, padding: 16, background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>
              Configure Bring-Your-Own (BYO) Azure Storage
            </span>
            <button type="button" onClick={() => setShowByoForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}>
              <FaTimes size={12} />
            </button>
          </div>
          <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 12px', lineHeight: 1.5 }}>
            <FaLock size={10} style={{ marginRight: 4 }} />
            <strong>Write-Only Credential:</strong> Connection string is sent securely over TLS for server validation and is immediately cleared from client memory. Plaintext keys are never persisted or displayed.
          </p>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }} htmlFor="byo-conn-str">
              Azure Blob Storage Connection String
            </label>
            <input
              id="byo-conn-str"
              type="password"
              value={byoConnectionString}
              onChange={(e) => setByoConnectionString(e.target.value)}
              placeholder="DefaultEndpointsProtocol=https;AccountName=...;AccountKey=...;EndpointSuffix=core.windows.net"
              autoComplete="off"
              style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: 13, fontFamily: 'monospace' }}
              disabled={actionInProgress === 'byo'}
              required
            />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => { setByoConnectionString(''); setShowByoForm(false); }}
              style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer', color: '#64748b' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={actionInProgress === 'byo' || !byoConnectionString.trim()}
              style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              {actionInProgress === 'byo' ? <FaSyncAlt className="spin" size={10} /> : null}
              Submit & Validate
            </button>
          </div>
        </form>
      )}

      {/* Rotate Credentials Form */}
      {showRotateForm && (
        <form onSubmit={handleRotateByo} style={{ marginTop: 16, padding: 16, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>
              Rotate Azure Storage Connection String
            </span>
            <button type="button" onClick={() => setShowRotateForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#92400e' }}>
              <FaTimes size={12} />
            </button>
          </div>
          <p style={{ fontSize: 12, color: '#78350f', margin: '0 0 12px' }}>
            Submit the new replacement Azure Storage connection string. The backend will validate connectivity before updating the account reference.
          </p>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#92400e', marginBottom: 4 }} htmlFor="rotate-conn-str">
              New Azure Storage Connection String
            </label>
            <input
              id="rotate-conn-str"
              type="password"
              value={rotateConnectionString}
              onChange={(e) => setRotateConnectionString(e.target.value)}
              placeholder="DefaultEndpointsProtocol=https;AccountName=...;AccountKey=...;EndpointSuffix=core.windows.net"
              autoComplete="off"
              style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #fcd34d', fontSize: 13, fontFamily: 'monospace' }}
              disabled={actionInProgress === 'rotate'}
              required
            />
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => { setRotateConnectionString(''); setShowRotateForm(false); }}
              style={{ background: '#fff', border: '1px solid #fcd34d', borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer', color: '#92400e' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={actionInProgress === 'rotate' || !rotateConnectionString.trim()}
              style={{ background: '#d97706', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              {actionInProgress === 'rotate' ? <FaSyncAlt className="spin" size={10} /> : null}
              Rotate & Validate
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

OrgStorageManagement.propTypes = {
  organizationId: PropTypes.string.isRequired,
  canManage: PropTypes.bool
};
